import * as React from "react";
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { AlertTriangle, RotateCcw, Sparkles } from "lucide-react";

/**
 * Describe-it-and-prefill trigger for create forms (Phase SC-22e).
 *
 * The admin types a plain description, the host's `generate` turns it into a
 * structured draft, and the sheet shows what will be filled in. "Use this
 * draft" hands the draft to `onApply`, which only sets form state — the host
 * form is still submitted by the admin, so nothing is ever saved from here.
 *
 * The component is network-free: the host injects `generate` (wired to
 * `POST /api/ai/draft/{kind}`) and `describe` (how a draft reads in the
 * review list), so every form keeps its own types and wording.
 */

export const AI_DRAFT_MIN_PROMPT = 3;
export const AI_DRAFT_MAX_PROMPT = 4000;

export interface AIDraftResult<TDraft> {
  draft: TDraft;
  explanation: string;
  warnings: string[];
}

export interface AIDraftPreviewRow {
  label: string;
  value: string;
}

export type AIDraftErrorVariant = "unavailable" | "unusable" | "denied" | "generic";

export interface AIDraftErrorInfo {
  variant: AIDraftErrorVariant;
  message: string;
}

export const AI_UNAVAILABLE_MESSAGE = "AI isn't available right now — fill the form manually.";
const UNUSABLE_MESSAGE =
  "The AI's answer couldn't be used. Try describing it differently, or fill the form manually.";
const GENERIC_MESSAGE = "Something went wrong. Try again, or fill the form manually.";

/** Map a thrown error onto what the admin should do next. Cost cap, rate
 *  limit, provider outage and network failures all read as "not available —
 *  fill it in yourself"; the form stays fully usable either way. */
export function classifyAIDraftError(err: unknown): AIDraftErrorInfo {
  if (err instanceof ApiError) {
    if (err.status === 429 || err.status === 503 || err.status === 0) {
      return { variant: "unavailable", message: AI_UNAVAILABLE_MESSAGE };
    }
    if (err.status === 502) return { variant: "unusable", message: UNUSABLE_MESSAGE };
    if (err.status === 403 || err.status === 404 || err.status === 422) {
      return { variant: "denied", message: err.message || GENERIC_MESSAGE };
    }
    if (err.status >= 500) return { variant: "unavailable", message: AI_UNAVAILABLE_MESSAGE };
    return { variant: "generic", message: err.message || GENERIC_MESSAGE };
  }
  return { variant: "unavailable", message: AI_UNAVAILABLE_MESSAGE };
}

type DraftState<TDraft> =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; error: AIDraftErrorInfo }
  | { status: "ready"; result: AIDraftResult<TDraft> };

export interface AIDraftButtonProps<TDraft> {
  /** Sheet title, e.g. "Draft a fee plan". */
  title: string;
  /** One line under the title explaining what will be filled in. */
  description?: string;
  /** Example description for the prompt box. */
  placeholder: string;
  /** Trigger label. Defaults to "Draft with AI". */
  label?: string;
  /** Turn a description into a draft. Rejections are shown in the sheet. */
  generate: (prompt: string) => Promise<AIDraftResult<TDraft>>;
  /** Put the draft into the form. Must not submit it. */
  onApply: (draft: TDraft) => void;
  /** Rows shown for review before the draft is used. */
  describe: (draft: TDraft) => AIDraftPreviewRow[];
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function AIDraftButton<TDraft>({
  title,
  description = "Describe it in your own words. You'll review everything in the form before saving.",
  placeholder,
  label = "Draft with AI",
  generate,
  onApply,
  describe,
  disabled,
  className,
  "data-testid": testId,
}: AIDraftButtonProps<TDraft>) {
  const [open, setOpen] = React.useState(false);
  const [prompt, setPrompt] = React.useState("");
  const [state, setState] = React.useState<DraftState<TDraft>>({ status: "idle" });
  const requestId = React.useRef(0);
  const lastPrompt = React.useRef<string | null>(null);

  const trimmed = prompt.trim();
  const promptValid =
    trimmed.length >= AI_DRAFT_MIN_PROMPT && trimmed.length <= AI_DRAFT_MAX_PROMPT;

  const run = React.useCallback(
    (text: string) => {
      lastPrompt.current = text;
      const id = ++requestId.current;
      setState({ status: "loading" });
      generate(text).then(
        (result) => {
          if (id === requestId.current) setState({ status: "ready", result });
        },
        (err: unknown) => {
          if (id === requestId.current)
            setState({ status: "error", error: classifyAIDraftError(err) });
        },
      );
    },
    [generate],
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) requestId.current++;
    setOpen(next);
    if (!next && state.status === "loading") setState({ status: "idle" });
  };

  const handleUse = () => {
    if (state.status !== "ready") return;
    onApply(state.result.draft);
    requestId.current++;
    setState({ status: "idle" });
    setPrompt("");
    setOpen(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (promptValid && state.status !== "loading") run(trimmed);
  };

  const rows = state.status === "ready" ? describe(state.result.draft) : [];

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={className}
        data-testid={testId}
      >
        <Sparkles aria-hidden />
        {label}
      </Button>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="size-4" aria-hidden />
              {title}
            </SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>

          <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
            <Textarea
              name="ai-draft-prompt"
              aria-label="Describe what you want to add"
              placeholder={placeholder}
              value={prompt}
              maxLength={AI_DRAFT_MAX_PROMPT}
              rows={4}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-[11px] tabular-nums">
                {trimmed.length}/{AI_DRAFT_MAX_PROMPT}
              </span>
              <Button type="submit" size="sm" disabled={!promptValid || state.status === "loading"}>
                {state.status === "loading" ? "Drafting…" : "Draft it"}
              </Button>
            </div>
          </form>

          {state.status === "loading" ? (
            <div className="flex flex-col gap-2" aria-live="polite" aria-busy="true">
              <p className="text-muted-foreground text-sm">Drafting…</p>
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-muted h-9 rounded-md motion-safe:animate-pulse" />
              ))}
            </div>
          ) : null}

          {state.status === "error" ? (
            <div
              role="alert"
              className="border-border bg-muted/40 flex flex-col gap-3 rounded-md border p-4"
            >
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
                <span>{state.error.message}</span>
              </div>
              {state.error.variant !== "denied" && lastPrompt.current ? (
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => lastPrompt.current && run(lastPrompt.current)}
                  >
                    <RotateCcw aria-hidden />
                    Try again
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {state.status === "ready" ? (
            <section aria-label="Draft preview" className="flex min-h-0 flex-1 flex-col gap-3">
              {state.result.explanation ? (
                <p className="text-muted-foreground text-sm">{state.result.explanation}</p>
              ) : null}
              <dl className="divide-border/60 min-h-0 flex-1 divide-y overflow-y-auto rounded-md border">
                {rows.length === 0 ? (
                  <div className="text-muted-foreground px-3 py-2 text-sm">
                    Nothing could be filled in from that description.
                  </div>
                ) : (
                  rows.map((row, index) => (
                    <div key={`${row.label}-${index}`} className="flex gap-3 px-3 py-2 text-sm">
                      <dt className="text-muted-foreground w-32 shrink-0">{row.label}</dt>
                      <dd className="min-w-0 flex-1 break-words">{row.value}</dd>
                    </div>
                  ))
                )}
              </dl>
              {state.result.warnings.length > 0 ? (
                <ul className="text-muted-foreground list-disc space-y-0.5 pl-5 text-xs">
                  {state.result.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : null}
              <div className="border-border flex items-center justify-end gap-2 border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => lastPrompt.current && run(lastPrompt.current)}
                >
                  <RotateCcw aria-hidden />
                  Regenerate
                </Button>
                <Button type="button" onClick={handleUse} disabled={rows.length === 0}>
                  Use this draft
                </Button>
              </div>
            </section>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
